import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { faPlayCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import SparkelIcon from '../common/icon/SparkelIcon';
import { InsufficientCreditsModal } from '../common/InsufficientCreditsModal';
import { PreventCloseContext } from './AutoshapePopover';
import { AutoShapeAlgorithmType, AutoShapeDeepFragment } from 'src/gql/graphql';
import {
  AlgorithmSettingsProps,
  algorithmSettingsRegistry,
  AutoShapeAlgorithmOptionsMap,
} from 'src/components/auto-shapes/types';
import { getAlgorithmMetadata, useAutoShapes } from 'src/hooks/auto-shapes';
import { useCredits, useTenantCredits } from 'src/hooks/credits';

type Props = {
  autoShape: AutoShapeDeepFragment;
  isOpen: boolean;
  onClose: () => void;
  onPreventCloseChange?: (preventClose: boolean) => void;
  anchorPosition: { x: number; y: number } | null;
};

// eslint-disable-next-line complexity
export function EditAutoshapePopover({
  autoShape,
  isOpen,
  onClose,
  onPreventCloseChange,
  anchorPosition,
}: Props) {
  const { t } = useTranslation('auto-shapes');
  const { t: tHeader } = useTranslation('header');
  const creditsInfo = useCredits(autoShape.projectId);
  const { credits: tenantCredits } = useTenantCredits();
  const {
    isOpen: isCreditsModalOpen,
    onOpen: openCreditsModal,
    onClose: closeCreditsModal,
  } = useDisclosure();

  const [isValid, setIsValid] = useState(true);
  const [preventClose, setPreventClose] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Use refs for synchronous access in event handlers
  const preventCloseRef = useRef(false);
  const onCloseRef = useRef(onClose);

  // Keep onCloseRef updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handlePreventCloseChange = useCallback((value: boolean) => {
    preventCloseRef.current = value;
    setPreventClose(value);
  }, []);

  // Notify parent when preventClose state changes
  useEffect(() => {
    onPreventCloseChange?.(preventClose);
  }, [preventClose, onPreventCloseChange]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isInsidePopover = containerRef.current?.contains(target);

      // Always ignore clicks inside the popover
      if (isInsidePopover) {
        return;
      }

      // Ignore clicks inside modals (e.g. InsufficientCreditsModal)
      if (target.closest?.('.chakra-modal__overlay, .chakra-modal__content')) {
        return;
      }

      // Check preventClose ref synchronously (settings components can request to prevent closing)
      if (preventCloseRef.current) {
        return;
      }

      onCloseRef.current();
    };

    // Delay adding the listener to avoid immediate close from the menu click
    const timeoutId = setTimeout(() => {
      // Use capture phase to catch events before they're stopped
      document.addEventListener('click', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Local editable options copied from the autoshape
  const initialOptions = useMemo(() => {
    return (autoShape.algorithmInput ?? {}) as Record<string, unknown>;
  }, [autoShape.algorithmInput]);

  const [options, setOptions] =
    useState<Record<string, unknown>>(initialOptions);

  // Reset options when popover opens or autoshape changes
  useEffect(() => {
    if (isOpen) {
      setOptions(initialOptions);
    }
  }, [initialOptions, isOpen]);

  const { updateAutoShape } = useAutoShapes();

  const SettingsComponent = useMemo(() => {
    return algorithmSettingsRegistry[
      autoShape.algorithmType as AutoShapeAlgorithmType
    ] as React.ComponentType<AlgorithmSettingsProps<AutoShapeAlgorithmType>>;
  }, [autoShape.algorithmType]);

  const algoMeta = getAlgorithmMetadata(
    autoShape.algorithmType as AutoShapeAlgorithmType
  );

  const creditsData = useMemo(() => {
    const creditCost = algoMeta.creditCost;
    const hasEnoughCredits =
      creditsInfo.unlimited ||
      creditCost === 0 ||
      creditsInfo.available >= creditCost;
    const creditCostLabel =
      creditCost > 0
        ? t('credit-cost.label', {
            count: creditCost,
            unit: algoMeta.creditCostUnitKey
              ? t(`${algoMeta.creditCostUnitKey}`)
              : '',
          })
        : null;
    const availableCredits = creditsInfo.isProjectScoped
      ? creditsInfo.available
      : tenantCredits?.availableCredits ?? 0;
    const creditsTooltipLabel = tHeader('credits.available-count', {
      count: availableCredits,
    });

    return {
      creditCost,
      hasEnoughCredits,
      creditCostLabel,
      creditsTooltipLabel,
    };
  }, [
    algoMeta.creditCost,
    algoMeta.creditCostUnitKey,
    creditsInfo.unlimited,
    creditsInfo.available,
    creditsInfo.isProjectScoped,
    tenantCredits?.availableCredits,
    t,
    tHeader,
  ]);

  const onRun = async () => {
    if (!creditsData.hasEnoughCredits) {
      openCreditsModal();
      return;
    }

    await updateAutoShape(
      autoShape.id,
      autoShape.algorithmType as AutoShapeAlgorithmType,
      options as unknown as AutoShapeAlgorithmOptionsMap[AutoShapeAlgorithmType]
    );

    onClose();
  };

  if (!isOpen || !anchorPosition) {
    return null;
  }

  return (
    <Portal>
      <Box
        ref={containerRef}
        data-edit-autoshape-popover="true"
        position="fixed"
        left={`${anchorPosition.x}px`}
        top={`${anchorPosition.y}px`}
        transform="translateX(-100%)"
        bg="white"
        _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
        borderRadius="xl"
        boxShadow="lg"
        border="1px solid"
        borderColor="gray.200"
        p={3}
        width="300px"
        zIndex={9999}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <PreventCloseContext.Provider value={handlePreventCloseChange}>
          <VStack align="stretch" spacing={3}>
            <Heading size="sm">{algoMeta.name}</Heading>
            {SettingsComponent && (
              <SettingsComponent
                value={
                  options as unknown as AutoShapeAlgorithmOptionsMap[AutoShapeAlgorithmType]
                }
                onChange={(val) =>
                  setOptions(val as unknown as Record<string, unknown>)
                }
                onValidityChange={setIsValid}
              />
            )}
            <Divider />
            <HStack width="100%" justifyContent="flex-end" spacing={2}>
              {creditsData.creditCostLabel && (
                <Tooltip
                  label={creditsData.creditsTooltipLabel}
                  hasArrow
                  openDelay={400}
                >
                  <Text
                    fontSize="xs"
                    color={
                      creditsData.hasEnoughCredits ? 'purple.500' : 'orange.500'
                    }
                    fontStyle="italic"
                  >
                    {creditsData.creditCostLabel}
                  </Text>
                </Tooltip>
              )}
              <Button
                isDisabled={creditsData.hasEnoughCredits && !isValid}
                size="xs"
                borderRadius="md"
                colorScheme={creditsData.hasEnoughCredits ? 'purple' : 'orange'}
                variant="outline"
                leftIcon={
                  <SparkelIcon
                    icon={
                      creditsData.hasEnoughCredits ? faPlayCircle : faPlusCircle
                    }
                    color={'inherit'}
                  />
                }
                onClick={onRun}
              >
                {creditsData.hasEnoughCredits ? t('run') : t('add-credits')}
              </Button>
            </HStack>
          </VStack>
        </PreventCloseContext.Provider>
      </Box>
      {isCreditsModalOpen && (
        <InsufficientCreditsModal
          isOpen={isCreditsModalOpen}
          onClose={closeCreditsModal}
          creditsNeeded={creditsData.creditCost}
          creditsAvailable={creditsInfo.available}
          isProjectScoped={creditsInfo.isProjectScoped}
          renewalDate={creditsInfo.renewalDate}
          renewalAmount={creditsInfo.renewalAmount}
        />
      )}
    </Portal>
  );
}
